/**
 * Care Plan Utilities
 *
 * Utility functions for the Care Plan component.
 * Handles API response transformation and display logic.
 */

/**
 * Transform API response to the component's expected format.
 * Handles both camelCase and snake_case field names from the API.
 *
 * Actual API response shape:
 *   recommendations: { caregiving_staff: "...", therapists: "...", clinicians: "..." }
 *   insights.mobility: { risk_tier, report, performance: { transitions, gait, balance, turning }, wellness_report_url }
 *   insights.vitals: { metrics: [{ key, value }] }    (value "-1" = no data)
 *   insights.activity: { pfall, night_wandering }
 */
export const transformCarePlanResponse = (data) => {
  if (!data) return null;

  // Handle top-level fields (camelCase or snake_case)
  const lastUpdated =
    data.lastUpdated || data.last_updated || data.updatedAt || data.updated_at;

  // Transform recommendations — handle both array and object formats
  const rawRecs =
    data.recommendations || data.care_action_plan || data.action_plan || [];
  let recommendations;

  if (Array.isArray(rawRecs)) {
    // Array format (e.g. [{ role, label, text }])
    recommendations = rawRecs.map((rec) => ({
      role: rec.role || rec.role_key || "",
      label: rec.label || rec.role_label || "",
      icon: rec.icon || mapRoleToIcon(rec.role || rec.role_key || ""),
      text: rec.text || rec.description || rec.recommendation || "",
    }));
  } else if (typeof rawRecs === "object" && rawRecs !== null) {
    // Object format from API: { caregiving_staff: "...", therapists: "...", clinicians: "..." }
    recommendations = Object.entries(rawRecs).map(([key, text]) => ({
      role: key,
      label: formatRoleLabel(key),
      icon: mapRoleToIcon(key),
      text: text || "",
    }));
  } else {
    recommendations = [];
  }

  // Transform insights
  const rawInsights = data.insights || data.input_insights || {};

  // Transform mobility
  const rawMobility = rawInsights.mobility || null;
  const mobility = rawMobility
    ? {
        riskTier: transformRiskTier(rawMobility),
        reportId:
          rawMobility.reportId ||
          rawMobility.report_id ||
          rawMobility.report ||
          null,
        latestRiskAnalysisUrl:
          rawMobility.latestRiskAnalysisUrl ||
          rawMobility.latest_risk_analysis_url ||
          rawMobility.wellnessReportUrl ||
          rawMobility.wellness_report_url ||
          null,
        metricsPerformance: transformMobilityPerformance(rawMobility),
        wellnessReportUrl:
          rawMobility.wellnessReportUrl ||
          rawMobility.wellness_report_url ||
          null,
      }
    : null;

  // Transform activity — handle both nested fallAlerts and flat { pfall, night_wandering }
  const rawActivity = rawInsights.activity || null;
  const activity = rawActivity
    ? {
        fallAlerts: transformFallAlerts(rawActivity),
        abnormalities: (
          rawActivity.abnormalities ||
          rawActivity.anomalies ||
          []
        ).map((a) => ({
          type: a.type || a.anomaly_type || "",
          label: a.label || a.anomaly_label || "",
          description: a.description || "",
          status: a.status || "fair",
        })),
      }
    : null;

  // Transform vitals — handle "-1" as no-data
  const rawVitals = rawInsights.vitals || null;
  const vitals = rawVitals
    ? {
        metrics: (rawVitals.metrics || []).map((v) => {
          const rawValue = v.displayValue || v.display_value || v.value || "";
          const isNoData =
            rawValue === "-1" || rawValue === -1 || rawValue === "";
          const rawStatus = v.status || v.metric_status || v.level || "";
          const normalizedStatus = rawStatus
            ? String(rawStatus).trim().toLowerCase()
            : "";
          return {
            key: v.key || v.vital_key || "",
            label: v.label || v.vital_label || v.key || "",
            fullLabel: v.fullLabel || v.full_label || v.label || v.key || "",
            displayValue: isNoData ? "N/A" : rawValue,
            unit: v.unit || "",
            status: isNoData ? "no_data" : normalizedStatus || "good",
            statusLabel: isNoData
              ? "No Data"
              : capitalizeStatus(normalizedStatus || "good"),
          };
        }),
      }
    : null;

  return {
    lastUpdated,
    recommendations,
    insights: {
      mobility,
      activity,
      vitals,
    },
  };
};

/**
 * Map role key to icon name
 */
const mapRoleToIcon = (role) => {
  if (!role) return "caregiving";
  const roleLower = role.toLowerCase();
  if (roleLower.includes("caregiv")) return "caregiving";
  if (roleLower.includes("therapist") || roleLower.includes("pt"))
    return "therapist";
  if (roleLower.includes("clinician") || roleLower.includes("doctor"))
    return "clinician";
  return "caregiving";
};

/**
 * Format a snake_case role key into a display label
 * e.g. "caregiving_staff" -> "Caregiving Staff"
 */
const formatRoleLabel = (roleKey) => {
  const labels = {
    caregiving_staff: "Caregiving Staff",
    therapists: "Therapists",
    clinicians: "Clinicians",
  };
  return (
    labels[roleKey] ||
    roleKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

/**
 * Capitalize a status string (e.g., "elevated" -> "Elevated Risk")
 */
const capitalizeStatus = (status) => {
  if (!status) return "";
  const normalized = String(status).trim().toLowerCase();
  const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
  if (["elevated", "high", "moderate"].includes(normalized)) {
    return `${label} Risk`;
  }
  return label;
};

const normalizeMetricStatus = (status) => {
  if (!status) return "";
  return String(status).trim().toLowerCase();
};

const getCountFromMetric = (metric) => {
  if (metric == null) return 0;
  if (typeof metric === "number") return metric;
  if (typeof metric === "object") {
    const raw = metric.value ?? metric.count ?? 0;
    return typeof raw === "number" ? raw : Number(raw) || 0;
  }
  return 0;
};

const getStatusFromMetric = (metric, count, fallbackGood = "good") => {
  if (metric && typeof metric === "object" && metric.status) {
    return normalizeMetricStatus(metric.status);
  }
  if (count > 0) return "poor";
  return fallbackGood;
};

/** Map activity level from API to StatusMetricBlock status: medium→fair, high→poor, else→good */
const levelToStatus = (level) => {
  if (!level) return "good";
  const s = String(level).trim().toLowerCase();
  if (s === "high") return "poor";
  if (s === "medium") return "fair";
  return "good";
};

const normalizeRiskTierKey = (val) => {
  if (!val) return null;
  const s = String(val).trim().toLowerCase();
  if (["high", "elevated", "low"].includes(s)) return s;
  if (s === "moderate" || s === "medium") return "elevated";
  if (s.includes("high")) return "high";
  if (s.includes("elevated")) return "elevated";
  if (s.includes("low")) return "low";
  return s;
};

const riskTierConfig = (key) => {
  const k = normalizeRiskTierKey(key);
  if (k === "high")
    return { level: "high", label: "High Risk of Fall", status: "poor" };
  if (k === "elevated")
    return { level: "elevated", label: "Elevated Fall Risk", status: "fair" };
  if (k === "low")
    return { level: "low", label: "Low Fall Risk", status: "good" };
  if (!k) return { level: null, label: "", status: null };
  // Fallback for unexpected strings
  return { level: k, label: capitalizeStatus(k), status: k };
};

/**
 * Transform risk_tier from API.
 * Handles: null, string, or object { level, label }
 */
const transformRiskTier = (rawMobility) => {
  const rawTier = rawMobility.riskTier || rawMobility.risk_tier || null;

  if (rawTier === null || rawTier === undefined) {
    return { level: null, label: "", status: null };
  }
  if (typeof rawTier === "string") {
    return riskTierConfig(rawTier);
  }
  // Object with level / label
  const rawLevel =
    rawTier.level || rawMobility.riskLevel || rawMobility.risk_level || null;
  const configFromLevel = riskTierConfig(rawLevel);
  const labelOverride =
    rawTier.label || rawMobility.riskLabel || rawMobility.risk_label || null;

  return {
    ...configFromLevel,
    label: labelOverride || configFromLevel.label,
  };
};

/**
 * Transform mobility performance metrics.
 * Handles:
 *   - Array: [{ key, label, status }]
 *   - Object (API): { transitions: null, gait: null, balance: null, turning: null }
 */
const transformMobilityPerformance = (rawMobility) => {
  const raw =
    rawMobility.metricsPerformance ||
    rawMobility.metrics_performance ||
    rawMobility.metrics ||
    rawMobility.performance ||
    [];

  if (Array.isArray(raw)) {
    return raw.map((m) => ({
      key: m.key || m.metric_key || "",
      label: m.label || m.metric_label || "",
      status: m.status || "good",
      statusLabel:
        m.statusLabel || m.status_label || capitalizeStatus(m.status || "good"),
    }));
  }

  if (typeof raw === "object" && raw !== null) {
    // Object format: { transitions: "fair", gait: null, ... }
    return Object.entries(raw).map(([key, value]) => {
      const status = value ? String(value).trim().toLowerCase() : "no_data";
      return {
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        status,
        statusLabel: value ? capitalizeStatus(status) : "No Data",
      };
    });
  }

  return [];
};

/**
 * Transform fall alerts from API to component format.
 * Handles:
 *   - New flat API: { pfall, pfall_level, fall, fall_level, night_wandering, from, to }
 *     - Levels map to status: medium→fair (yellow), high→poor (red), else→good (green).
 *     - When fall is present (count > 0), only Fall is shown (red). Else pfall is shown with pfall_level color.
 *     - night_wandering === null → not shown; otherwise shown with count/status.
 *   - Nested: { fallAlerts: { pfall: { count, frequency, status }, fall: {...} } }
 *   - Legacy flat: { pfall: null|number, night_wandering: null|number }
 */
const transformFallAlerts = (rawActivity) => {
  // New API shape: pfall, pfall_level, fall, fall_level, night_wandering
  const hasNewShape =
    rawActivity &&
    (rawActivity.pfall_level != null ||
      rawActivity.fall_level != null ||
      rawActivity.pfall != null ||
      rawActivity.fall != null);

  if (hasNewShape) {
    const pfallCount = getCountFromMetric(rawActivity.pfall);
    const fallCount = getCountFromMetric(rawActivity.fall);
    const pfallStatus = levelToStatus(rawActivity.pfall_level);
    const fallStatus =
      fallCount > 0 ? "poor" : levelToStatus(rawActivity.fall_level);

    const nightWanderingRaw = rawActivity.night_wandering;
    const nwStatus = levelToStatus(
      nightWanderingRaw?.level ?? rawActivity.night_wandering_level,
    );
    const nightWandering =
      nightWanderingRaw == null
        ? null
        : {
            count: getCountFromMetric(nightWanderingRaw),
            frequency:
              nightWanderingRaw?.frequency ||
              rawActivity.night_wandering_frequency ||
              "times / week",
            status: nwStatus,
            statusLabel: capitalizeStatus(nwStatus),
          };

    return {
      pfall: {
        count: pfallCount,
        frequency: rawActivity.pfall?.frequency || "times / week",
        status: pfallStatus,
        statusLabel: capitalizeStatus(pfallStatus),
      },
      fall: {
        count: fallCount,
        frequency: rawActivity.fall?.frequency || "times / week",
        status: fallStatus,
        statusLabel: capitalizeStatus(fallStatus),
      },
      nightWandering,
    };
  }

  // Check for nested fallAlerts / fall_alerts
  const alerts = rawActivity.fallAlerts || rawActivity.fall_alerts || null;

  if (alerts && typeof alerts === "object") {
    return {
      pfall: {
        count: alerts.pfall?.count ?? alerts.possible_fall?.count ?? 0,
        frequency:
          alerts.pfall?.frequency ||
          alerts.possible_fall?.frequency ||
          "times / week",
        status: alerts.pfall?.status || alerts.possible_fall?.status || "good",
      },
      fall: {
        count: alerts.fall?.count ?? alerts.night_wandering?.count ?? 0,
        frequency:
          alerts.fall?.frequency ||
          alerts.night_wandering?.frequency ||
          "times / week",
        status: alerts.fall?.status || alerts.night_wandering?.status || "good",
      },
      nightWandering: null,
    };
  }

  // Legacy flat format: { pfall: null|number, night_wandering: null|number }
  const pfallVal = rawActivity.pfall;
  const nightVal = rawActivity.night_wandering ?? rawActivity.fall;

  const pfallCount = getCountFromMetric(pfallVal);
  const nightCount = getCountFromMetric(nightVal);
  const pfallStatus = getStatusFromMetric(pfallVal, pfallCount, "good");
  const nightStatus = getStatusFromMetric(nightVal, nightCount, "good");

  return {
    pfall: {
      count: pfallCount,
      frequency: pfallVal?.frequency || "times / week",
      status: pfallStatus,
      statusLabel: capitalizeStatus(pfallStatus),
    },
    fall: {
      count: nightCount,
      frequency: nightVal?.frequency || "times / week",
      status: nightStatus,
      statusLabel: capitalizeStatus(nightStatus),
    },
    nightWandering:
      rawActivity.night_wandering == null
        ? null
        : {
            count: nightCount,
            frequency: nightVal?.frequency || "times / week",
            status: nightStatus,
            statusLabel: capitalizeStatus(nightStatus),
          },
  };
};

/**
 * Format date for display
 */
export const formatLastUpdated = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Check if mobility section should be shown
 */
export const shouldShowMobility = (mobility) => {
  if (!mobility) return false;
  const hasRiskTier = mobility.riskTier?.level != null;
  const hasElevatedRisk =
    mobility.riskTier?.level === "elevated" ||
    mobility.riskTier?.level === "high";
  const hasConcerningMetrics = mobility.metricsPerformance?.some(
    (m) => m.status === "fair" || m.status === "poor" || m.status === "no_data",
  );
  const hasMetrics = mobility.metricsPerformance?.length > 0;
  return hasElevatedRisk || hasConcerningMetrics || (hasRiskTier && hasMetrics);
};

/**
 * Check if activity section should be shown
 */
export const shouldShowActivity = (activity) => {
  if (!activity) return false;
  const alerts = activity.fallAlerts;
  const hasFall = (alerts?.fall?.count ?? 0) > 0;
  const hasPfall = (alerts?.pfall?.count ?? 0) > 0;
  const hasNightWandering = alerts?.nightWandering != null;
  const hasAbnormalities = activity.abnormalities?.length > 0;
  return hasFall || hasPfall || hasNightWandering || hasAbnormalities;
};

/**
 * Check if vitals section should be shown
 */
export const shouldShowVitals = (vitals) => {
  if (!vitals?.metrics) return false;
  return vitals.metrics.length > 0;
};

/**
 * Filter vitals to only show fair, poor, and no_data
 */
export const filterVitalsForDisplay = (metrics) => {
  if (!metrics) return [];
  const flagged = metrics.filter(
    (v) => v.status === "fair" || v.status === "poor" || v.status === "no_data",
  );
  return flagged.length > 0 ? flagged : metrics;
};
